import { useLazyQuery, useMutation } from '@apollo/client/react';

import { optlyEvents } from '@constants/trackingEvents';
import { useOptlyEvent } from '@features/Optimisely/useOptlyEvent';
import {
  addCarInsuranceRenewalDate,
  addHomeInsuranceRenewalDate,
  submitBroadbandRenewal as submitBroadbandRenewalDate,
  submitMobileRenewal as submitMobileRenewalDate,
} from '@features/details/Details/RenewalCapture/query';
import { gql } from '@generated/gql';
import { ProductType } from '@generated/gql/graphql';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { useDetailsContext } from '@providers/basket/DetailsProvider/Context';
import { useTrackingContext } from '@tracking/Provider/Context';
import { useTrackSignupContractExpiryDateSubmitted } from '@tracking/trackingEvents';

const getBasketAccountInfoQuery = gql(`
  query GetBasketAccountInfo($basketId: ID!) {
    basket(id: $basketId) {
      id
      details {
        accountId
        accountNumber
      }
      selectedQuotes {
        product {
          type
        }
      }
    }
  }
`);

const useSubmitRenewalCapture = () => {
  const { basketId } = useBasketContext();
  const [getBasketAccountInfo] = useLazyQuery(getBasketAccountInfoQuery, {
    fetchPolicy: 'no-cache',
  });
  const { details } = useDetailsContext();
  const [submitBroadbandRenewal] = useMutation(submitBroadbandRenewalDate);
  const [submitMobileRenewal] = useMutation(submitMobileRenewalDate);
  const [submitHomeInsuranceRenewal] = useMutation(addHomeInsuranceRenewalDate);
  const [submitCarInsuranceRenewal] = useMutation(addCarInsuranceRenewalDate);

  const { trackingContexts } = useTrackingContext();
  const trackDateSubmitted = useTrackSignupContractExpiryDateSubmitted();
  const optlyEvent = useOptlyEvent();

  const {
    renewalBroadbandDate,
    renewalMobileDate,
    renewalHomeInsuranceDate,
    renewalCarInsuranceDate,
  } = details;

  return async () => {
    const { data } = await getBasketAccountInfo({ variables: { basketId } });

    const basket = data?.basket;
    const details = basket?.details;
    const selectedQuotes = basket?.selectedQuotes ?? [];

    if (!details) return;

    const { accountId, accountNumber } = details;

    const selectedQuotesArray = selectedQuotes.map(
      (quote) => quote.product.type
    );

    const requestQueue = [];

    if (
      accountNumber &&
      renewalMobileDate &&
      !selectedQuotesArray.includes(ProductType.Mobile)
    ) {
      trackDateSubmitted({ product: 'Mobile' }, { contexts: trackingContexts });

      requestQueue.push(
        submitMobileRenewal({
          variables: {
            accountNumber: accountNumber,
            endDate: renewalMobileDate,
          },
        })
      );
    }

    if (
      accountId &&
      renewalBroadbandDate &&
      !selectedQuotesArray.includes(ProductType.Broadband)
    ) {
      trackDateSubmitted(
        { product: 'Broadband' },
        { contexts: trackingContexts }
      );

      requestQueue.push(
        submitBroadbandRenewal({
          variables: {
            accountNumber: accountId,
            endDate: renewalBroadbandDate,
          },
        })
      );
    }

    if (renewalHomeInsuranceDate) {
      trackDateSubmitted(
        { product: 'HomeInsurance' },
        { contexts: trackingContexts }
      );

      requestQueue.push(
        submitHomeInsuranceRenewal({
          variables: {
            input: {
              accountNumber: accountId ?? '',
              endDate: renewalHomeInsuranceDate,
            },
          },
        })
      );
    }

    if (renewalCarInsuranceDate) {
      trackDateSubmitted(
        { product: 'CarInsurance' },
        { contexts: trackingContexts }
      );

      requestQueue.push(
        submitCarInsuranceRenewal({
          variables: {
            accountNumber: accountId ?? '',
            endDate: renewalCarInsuranceDate,
          },
        })
      );
    }

    await Promise.allSettled(requestQueue);
    optlyEvent(optlyEvents.contractExpiryDateSubmitted);
  };
};

export { useSubmitRenewalCapture };
